import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Check, Sparkles, Users } from '../components/Icons.jsx';

const questions = [
  { id: 'weak', prompt: 'Where are you bleeding marks?', options: [
    { value: 'vr', label: 'Verbal Reasoning — pacing on the passages' },
    { value: 'dm', label: 'Decision Making — Venn / syllogism logic' },
    { value: 'qr', label: 'Quantitative Reasoning — speed without calculator' },
    { value: 'sjt', label: 'Situational Judgement — ranking ambiguous options' },
    { value: 'all', label: 'All sections — need a full strategy' }
  ]},
  { id: 'hours', prompt: 'Hours per week you can commit to tutoring + practice?', options: [
    { value: 'low', label: '1–3 hours · Light touch alongside school' },
    { value: 'mid', label: '4–7 hours · Serious prep' },
    { value: 'high', label: '8+ hours · Intensive (close to test)' }
  ]},
  { id: 'date', prompt: 'How close is your test date?', options: [
    { value: 'soon', label: 'Within 4 weeks' },
    { value: 'mid', label: '1–3 months' },
    { value: 'far', label: '3–6 months' },
    { value: 'planning', label: 'Still in planning stage' }
  ]},
  { id: 'level', prompt: 'Where are your mock scores landing?', options: [
    { value: 'low', label: 'Under 2200 — building foundations' },
    { value: 'mid', label: '2200–2400 — solid but plateauing' },
    { value: 'high', label: '2400–2600 — pushing for top-school territory' },
    { value: 'elite', label: '2600+ — chasing the last 100 points' },
    { value: 'none', label: "Haven't taken a mock yet" }
  ]},
  { id: 'budget', prompt: 'Comfort with tutoring budget?', options: [
    { value: 'low', label: 'Group sessions only / lowest-cost path' },
    { value: 'med', label: '1-to-1, standard rate' },
    { value: 'high', label: 'Premium — top tutors, intensive blocks' }
  ]}
];

const tutors = [
  { name: 'Aarav Patel', score: 3320, scored: '2900 in real UCAT', specialty: ['qr', 'dm', 'all'], hours: ['low', 'mid', 'high'], date: ['soon', 'mid', 'far', 'planning'], level: ['low', 'mid', 'high', 'elite', 'none'], budget: ['med', 'high'], bio: 'King\'s College London Y4. Coaches QR and DM with the "skip-and-park" method that saved her 12 marks. 250+ hours coached.', rate: '£55/hr' },
  { name: 'Priya Sharma', score: 3290, scored: '2870 in real UCAT, Band 1', specialty: ['sjt', 'vr', 'all'], hours: ['low', 'mid', 'high'], date: ['mid', 'far', 'planning'], level: ['low', 'mid', 'high', 'none'], budget: ['med', 'high'], bio: 'UCL Y3. SJT specialist — built the GMC-framework drill that 89% of her students Band-1 on. Plus VR pacing.', rate: '£60/hr' },
  { name: 'James Chen', score: 3340, scored: '2920 in real UCAT', specialty: ['vr', 'dm', 'qr', 'all'], hours: ['mid', 'high'], date: ['soon', 'mid', 'far'], level: ['mid', 'high', 'elite'], budget: ['high'], bio: 'Imperial Y5, on-track for academic foundation programme. Coaches the 2600+ tier looking for the last 100 marks. Test-week intensives only.', rate: '£85/hr' },
  { name: 'Sofia Akhtar', score: 3260, scored: '2840 in real UCAT', specialty: ['vr', 'sjt', 'all'], hours: ['low', 'mid'], date: ['mid', 'far', 'planning'], level: ['low', 'mid', 'none'], budget: ['low', 'med'], bio: 'Manchester Y2. Friendly, methodical, great with students starting from scratch. Small-group sessions available.', rate: '£40/hr' },
  { name: 'Daniel Okafor', score: 3300, scored: '2890 in real UCAT', specialty: ['dm', 'qr', 'all'], hours: ['mid', 'high'], date: ['soon', 'mid'], level: ['mid', 'high'], budget: ['med', 'high'], bio: 'Edinburgh Y4. DM/QR specialist — the "process-of-elimination decision tree" approach. Calm under exam pressure.', rate: '£65/hr' },
  { name: 'Anya Kovalenko', score: 3280, scored: '2880 in real UCAT, Band 1', specialty: ['sjt', 'all'], hours: ['low', 'mid'], date: ['mid', 'far'], level: ['mid', 'high'], budget: ['med'], bio: 'Cambridge Y3. SJT and interview coaching specialist — has 100% offer rate for students who do both.', rate: '£70/hr' },
  { name: 'Marcus Thompson', score: 3250, scored: '2820 in real UCAT', specialty: ['qr', 'vr', 'all'], hours: ['low', 'mid', 'high'], date: ['soon', 'mid', 'far'], level: ['low', 'mid', 'none'], budget: ['low', 'med'], bio: 'Newcastle Y3. Patient, encouraging style — works well with students who panic under timed conditions.', rate: '£45/hr' }
];

function scoreTutor(t, answers) {
  let score = 0;
  if (t.specialty.includes(answers.weak)) score += 30;
  if (t.hours.includes(answers.hours)) score += 15;
  if (t.date.includes(answers.date)) score += 15;
  if (t.level.includes(answers.level)) score += 25;
  if (t.budget.includes(answers.budget)) score += 15;
  return score;
}

export default function TutorMatch() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});

  const matched = useMemo(() => {
    if (step < questions.length) return [];
    return tutors.map((t) => ({ ...t, m: scoreTutor(t, answers) })).sort((a, b) => b.m - a.m).slice(0, 3);
  }, [step, answers]);

  if (step >= questions.length) {
    return (
      <>
        <div className="page-head">
          <div className="kicker" style={{ color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, fontSize: 12 }}>Your matches</div>
          <h1>Your top 3 Medic Mind tutors</h1>
          <p className="subtitle">Matched on the section you need, your timeline, your level, and your budget. Free 15-minute meet-and-greet with any tutor before you book.</p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {matched.map((t, i) => (
            <article key={t.name} className="card hoverable" style={{ display: 'flex', gap: 18, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div style={{ width: 72, height: 72, borderRadius: 16, background: 'linear-gradient(135deg, var(--brand-purple), var(--brand-deep))', color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 22, flexShrink: 0 }}>
                {t.name.split(' ').map((p) => p[0]).join('')}
              </div>
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
                  <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{t.name}</h3>
                  {i === 0 && <span className="pill pill-teal">Top match</span>}
                </div>
                <p style={{ margin: '4px 0', fontSize: 13, color: 'var(--accent)', fontWeight: 600 }}>{t.scored}</p>
                <p style={{ margin: '6px 0 10px', color: 'var(--text-2)', fontSize: 14 }}>{t.bio}</p>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ fontWeight: 700 }}>{t.rate}</span>
                  <a className="btn btn-primary" href="https://www.medicmind.co.uk/contact-us/" target="_blank" rel="noreferrer">Book free 15-min chat <ArrowRight width="16" height="16" /></a>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 22, flexWrap: 'wrap' }}>
          <button onClick={() => { setStep(0); setAnswers({}); }} className="btn btn-secondary">Re-run match</button>
          <Link to="/medi" className="btn btn-secondary">Not sure? Ask Ash first</Link>
        </div>
      </>
    );
  }

  const q = questions[step];
  const progress = ((step + 1) / questions.length) * 100;

  return (
    <>
      <div className="page-head">
        <div className="kicker" style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 600 }}>Question {step + 1} of {questions.length}</div>
        <h1>Find your tutor</h1>
        <p className="subtitle">5 questions. We match you to 3 tutors who fit.</p>
      </div>

      <div className="progress" style={{ marginBottom: 20, height: 6 }}>
        <div className="progress-bar" style={{ width: `${progress}%` }} />
      </div>

      <div className="card" style={{ marginBottom: 18 }}>
        <h2 style={{ margin: '0 0 18px', fontSize: 20 }}>{q.prompt}</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {q.options.map((opt) => (
            <button
              key={opt.value}
              onClick={() => { setAnswers({ ...answers, [q.id]: opt.value }); setStep(step + 1); }}
              style={{
                padding: '14px 18px', textAlign: 'left',
                border: `1px solid ${answers[q.id] === opt.value ? 'var(--brand-purple)' : 'var(--border)'}`,
                background: answers[q.id] === opt.value ? 'var(--accent-soft)' : '#fff',
                color: 'var(--text)', borderRadius: 12, fontSize: 15, fontWeight: 500, cursor: 'pointer'
              }}
            >{opt.label}</button>
          ))}
        </div>
      </div>

      {step > 0 && <button onClick={() => setStep(step - 1)} className="back-link"><ArrowLeft width="16" height="16" />Previous</button>}
    </>
  );
}
